import Phaser from 'phaser';
import { storyAnimateOutPiece, addPieceSpriteToBoard } from 'helpers/gameplay';
import { checkAndApplyIdleAnimation } from 'helpers/pieceHelpers';
import * as History from 'helpers/historyHelpers';
import * as Movement from 'helpers/movementHelpers';
import {
  SHADOW,
  UNWALKABLE,
  SWITCH_OPEN_BOTTOM,
  SWITCH_CLOSED_BOTTOM,
  SWITCH_CLOSED_TOP,
  SWITCH_OPEN_TOP,
  LOGIC_DOOR,
  CUSTOM_DOM_EVENTS,
  ZOOM_LEVELS
} from 'helpers/constants';

export function isInFreeRoam(scene) {
  return (
    (scene.sys.config === 'WorldScene' && !scene.battlingInWorld) ||
    scene.replay
  );
}

export function centerCameraOnStageCenter(scene, cb = () => {}) {
  if (scene.scene.key === 'CastleScene') {
    if (scene.previousSelectedPiece) {
      return scene.focusOnRoomWithPiece(scene.previousSelectedPiece, cb);
    } else {
      return scene.focusOnActiveRooms(cb);
    }
  }
  const cam = scene.cameras.main;
  let nextZoom =
    scene.scene.key === 'WorldScene' && !scene.battlingInWorld
      ? Math.max(scene.game.defaultZoom, 2)
      : scene.getZoomLevel();
  if (scene.scene.key === 'WorldScene' && scene.battlingInWorld) {
    const rect = new Phaser.Geom.Rectangle(
      scene.inZone.rect.x,
      scene.inZone.rect.y,
      scene.inZone.rect.w,
      scene.inZone.rect.h
    );
    nextZoom = scene.getZoomLevel(rect);
  }
  let zoomDone = false;
  let panDone = false;

  const checkToGoNext = () => {
    if (zoomDone && panDone) {
      cb();
    }
  };

  //zoomDone
  if (cam && nextZoom !== cam.zoom) {
    setTimeout(() => {
      cam.zoomTo(nextZoom, 650, 'Quad.easeInOut', true, (cam, progress) => {
        if (progress === 1) {
          zoomDone = true;
          checkToGoNext();
        }
      });
    }, 0);
  } else {
    zoomDone = true;
    checkToGoNext();
  }

  //panDone
  let nextCenter = scene.stageCenter;

  if (scene.getCenterOfActiveRoom) {
    nextCenter = scene.getCenterOfActiveRoom();
  }

  const alreadyAtNextCenter =
    nextCenter &&
    nextCenter.x === cam.scrollX + cam.centerX &&
    nextCenter.y === cam.scrollY + cam.centerY;
  if (nextCenter && !alreadyAtNextCenter) {
    cam.pan(
      nextCenter.x,
      nextCenter.y,
      650,
      'Quad.easeInOut',
      true,
      (cam, progress) => {
        if (progress === 1) {
          panDone = true;
          checkToGoNext();
        }
      }
    );
  } else {
    panDone = true;
    checkToGoNext();
  }
}
export function endSceneAndChildScenes(scene, cb = () => {}) {
  if (scene.game.datgui) {
    scene.game.datgui.destroy();
    scene.game.datgui = null;
    scene.logicLayerTools = null;
    scene.game.zoomGuage = null;
  }
  if (scene.hideTimer) {
    clearTimeout(scene.hideTimer);
  }

  const endScenes = () => {
    if (scene.scene.key === 'WorldScene') {
      scene.setWeather('sunshine');
    }
    scene.clearCurrentMenuOptions();
    scene.removeEventListeners();
    scene.scene.stop('GameScene');
    scene.scene.stop('DialogueScene');
    scene.scene.stop('StoryLevelScene');
    scene.scene.stop('CastleScene');
    scene.scene.stop('WorldScene');
    scene.scene.stop('PartyMenu');
    scene.scene.stop('WorldEditMenuScene');
    scene.scene.stop('ZoomControls');
    scene.scene.stop('GameMenuScene');
    scene.scene.stop('OptionsMainMenuScene');
    scene.scene.stop('CampaignTimelineEditor');
    cb();
  };

  if (scene.battlingInWorld) {
    clearInWorldBattleScene(scene, cb);
  } else {
    endScenes();
  }
}

export function clearInWorldBattleScene(scene, cb) {
  scene.setBattleZoneBounds?.(
    scene.inBattleLevelData,
    scene.inZone,
    false,
    () => {
      scene.inZone = null;
      scene.battleStarted = false;
      scene.battlingInWorld = false;
      scene.disableInput = false;
      scene.currentSelectedPiece = null;
      scene.previousSelectedPiece = null;
      scene.stageCenter = null;
      scene.pieces && scene.pieces.clear(true, true);
      endSceneAndChildScenes(scene, cb);
    }
  );
}

//
// command: { type: 'dialogue', x: 0, y: 0, portrait: 'Pp', text: 'I said this.'}
//
export function dialogue(scene, command, nextCommand, next) {
  const isWorldScene = scene.sys.config === 'WorldScene';

  if (isWorldScene && !scene.inZone) {
    scene.cameras.main.stopFollow();
  }

  scene.scene.launch('DialogueScene', {
    character: command.portrait,
    name: command.name,
    mood: command.mood,
    tile: scene.map.getTileAt(command.x, command.y, true, 'logic_layer'),
    scene: scene,
    text: command.text,
    // endOfDialogue: !nextCommand,
    options: command.options,
    end: () => {
      scene.scene.stop('DialogueScene');
      // for in-world NPCs, follow the unit after dialogue
      if (isWorldScene && !scene.inZone && !nextCommand) {
        console.log('following after dialogie comman chain...');
        scene.followUnit();
      }
      next();
    }
  });
}

//
// command: { type: 'wait', time: 1500 }
//
function wait(command, next) {
  window.dispatchEvent(
    new CustomEvent(CUSTOM_DOM_EVENTS.CommandDispatch, { detail: { command } })
  );
  setTimeout(next, command.time);
}

//
// command: { }
//
function recenterCamera(scene, command, next) {
  centerCameraOnStageCenter(scene, next);
}

//
// command: { }
//
function exitInWorldLevel(scene, command, next) {
  scene.endingScene();
  next();
}

//
// command: {type: 'animateOut', direction: 'up', sprite: Sprite, speed: 650 }
//
export function animateOut(scene, command, next) {
  storyAnimateOutPiece(
    scene,
    command.getSprite,
    command.direction,
    command.speed,
    () => {
      if (!command.skipSaveMapState) {
        History.saveMapState(
          scene,
          command.getSprite()?.color,
          command.replaceHistory
        );
      }
      next();
    }
  );
  // next();
}

//
// command: {type: 'animateIn', x: 0, y: 0, direction: 'up', pieceIndex: 271, speed: 650,  }
//
export function animateIn(scene, command, next) {
  const { x, y, pieceIndex, direction, speed, item } = command;
  const logicTile = scene.map.getTileAt(x, y, true, 'logic_layer');
  const piece = addPieceSpriteToBoard(
    scene,
    logicTile,
    item,
    pieceIndex,
    true,
    true,
    direction || 'down',
    speed || 400,
    () => {
      if (piece) {
        checkAndApplyIdleAnimation(scene, piece, { pieceIndex });
      }
      next();
    }
  );
}

//
// command: {type: 'addPiece', x: 0, y: 0, pieceIndex: 271, partyID: 1  }
//
export function addPiece(scene, command, next) {
  const { x, y, pieceIndex, item, partyID } = command;
  const logicTile = scene.map.getTileAt(x, y, true, 'logic_layer');
  let piece = addPieceSpriteToBoard(scene, logicTile, item, pieceIndex);
  if (!piece) {
    next();
    return;
  }
  let pieceIDs = scene.pieces.getChildren().map(p => p.id);
  if (scene.pieceMap) {
    const newID = partyID || partyID === 0 ? partyID : 200 + pieceIDs.length;

    piece.id = newID;

    scene.pieceMap[newID] = {
      x: x,
      y: y,
      id: newID,
      hp: piece.hp
    };
  }
  next();
}

//
// command: {type: 'movePiece', destination: [x, y], getSprite: () => Sprite }
//
export function movePiece(scene, command, next) {
  const { destination, getSprite, replaceHistory } = command;
  let piece = getSprite();
  let origin = piece && piece.tile && { x: piece.tile.x, y: piece.tile.y };
  if (!piece || !origin) {
    next();
    return;
  }
  const wasShaded = scene.map.getTileAt(origin.x, origin.y, true, 'shade_layer')
    .index;
  const nextMove = scene.map.getTileAt(
    destination[0],
    destination[1],
    true,
    'logic_layer'
  );
  if (!nextMove) {
    next();
    return;
  }
  scene.hideAbilityButton();
  scene.animateMove(piece, nextMove, piece.tile, false, true, unit => {
    piece = unit;
    Movement.clearTileHighlighting(scene.map);
    scene.deselectCurrentPiece();
    if (wasShaded > -1) {
      scene.map.putTileAt(SHADOW, origin.x, origin.y, true, 'shade_layer');
      scene.map.putTileAt(UNWALKABLE, origin.x, origin.y, true, 'logic_layer');
    }
    History.saveMapState(scene, piece.color, replaceHistory);
    next();
  });
}

//
// command: {type: 'function', action: function provided done callback (done) => {}}
//

function executeFunction(scene, command, next) {
  command.action(next);
}

//
// command: {type: 'resetHistory'}
//

function resetHistory(scene, command, next) {
  History.resetHistory(scene);
  History.saveMapState(scene, 'blue');
  next();
}

function executeNextCommand(scene, commandList, index, done) {
  let command = commandList[index];
  let recurse = () => {
    if (commandList[index + 1]) {
      executeNextCommand(scene, commandList, index + 1, done);
    } else {
      done();
    }
  };
  if (command.condition) {
    const allow = command.condition();
    if (!allow) {
      return recurse();
    }
  }
  switch (command.type) {
    case 'dialogue':
      dialogue(scene, command, commandList[index + 1], recurse);
      break;
    case 'wait':
      wait(command, recurse);
      break;
    case 'animateOut':
      animateOut(scene, command, recurse);
      break;
    case 'animateIn':
      animateIn(scene, command, recurse);
      break;
    case 'function':
      executeFunction(scene, command, recurse);
      break;
    case 'movePiece':
      movePiece(scene, command, recurse);
      break;
    case 'addPiece':
      addPiece(scene, command, recurse);
      break;
    case 'recenterCamera':
      recenterCamera(scene, command, recurse);
      break;
    case 'resetHistory':
      resetHistory(scene, command, recurse);
      break;
    case 'exitInWorldLevel':
      exitInWorldLevel(scene, command, recurse);
      break;
    case 'joinedPartyBanner':
      joinedPartyBanner(scene, command, recurse);
      break;
    default:
  }
}

export function commandChain(scene, done) {
  const commandList = arguments;
  scene.disableInput = true;
  scene.disableInputCommandChain = true;
  if (commandList[2]) {
    executeNextCommand(scene, commandList, 2, () => {
      scene.disableInput = false;
      scene.disableInputCommandChain = false;
      centerCameraOnStageCenter(scene, done);
    });
  } else {
    scene.disableInput = false;
    scene.disableInputCommandChain = false;
    centerCameraOnStageCenter(scene, done);
  }
}

export function toggleSwitchAt(scene, x, y, sprite) {
  if (sprite) {
    const animSwitchOpen = sprite.texture.key === 'anim_switch_off';
    if (animSwitchOpen) {
      sprite.setTexture('anim_switch_on');
      sprite.anims.remove('switch-off');
      sprite.anims.load('switch-on');
      sprite.anims.play('switch-on');
      scene.map.putTileAt(UNWALKABLE, x, y, true, 'logic_layer');
    } else {
      sprite.setTexture('anim_switch_off');
      sprite.anims.remove('switch-on');
      sprite.anims.load('switch-off');
      sprite.anims.play('switch-off');
    }
    return animSwitchOpen;
  } else {
    // non-animated switch
    const currentBottom = scene.map.getTileAt(x, y, true, 'below_player');
    const switchOpen = currentBottom.index === SWITCH_OPEN_BOTTOM;
    if (!switchOpen) {
      scene.map.putTileAt(SWITCH_OPEN_BOTTOM, x, y, true, 'below_player');
      scene.map.putTileAt(SWITCH_OPEN_TOP, x, y - 1, true, 'above_player');
      scene.map.putTileAt(UNWALKABLE, x, y, true, 'logic_layer');
    } else {
      scene.map.putTileAt(SWITCH_CLOSED_BOTTOM, x, y, true, 'below_player');
      scene.map.putTileAt(SWITCH_CLOSED_TOP, x, y - 1, true, 'above_player');
    }
    return switchOpen;
  }
}

export function revealBowShrine(scene, PanToWithCamera) {
  // move camera to the spot
  // convert tiles

  scene.map.putTileAt(762, 103, 63, true, 'below_player');
  scene.map.putTileAt(763, 104, 63, true, 'below_player');
  scene.map.putTileAt(764, 105, 63, true, 'decor_layer');

  scene.map.putTileAt(780, 103, 64, true, 'below_player');
  scene.map.putTileAt(781, 104, 64, true, 'below_player');
  scene.map.putTileAt(782, 105, 64, true, 'below_player');

  const doorTile = scene.map.putTileAt(
    LOGIC_DOOR,
    104,
    64,
    true,
    'logic_layer'
  );
  doorTile.goal = true;
  doorTile.doorTo = [
    'CastleScene',
    {
      index: 378
    }
  ];
}

export function hideBowShrine(scene, PanToWithCamera) {
  // move camera to the spot
  // convert tiles

  scene.map.removeTileAt(103, 63, false, true, 'below_player');
  scene.map.removeTileAt(104, 63, false, true, 'below_player');
  scene.map.removeTileAt(105, 63, false, true, 'decor_layer');

  scene.map.removeTileAt(103, 64, false, true, 'below_player');
  scene.map.removeTileAt(104, 64, false, true, 'below_player');
  scene.map.removeTileAt(105, 64, false, true, 'below_player');

  scene.map.removeTileAt(104, 64, true, 'logic_layer');
}

const joinsPartyPortraits = {
  Uncle: {
    portrait: 'ui_large_port_uncle',
    name: 'ui_joins_party_name_uncle'
  },
  Bishoff: {
    portrait: 'ui_large_port_bishoff',
    name: 'ui_joins_party_name_bishoff'
  }
};

function joinedPartyBanner(scene, command, next) {
  centerCameraOnStageCenter(scene, () => {
    scene.game.soundManager.playSfx('newPartyMember');
    const menuScene = scene.scene.get('GameMenuScene');
    const cam = menuScene.cameras.main;
    const scale =
      ZOOM_LEVELS[
        ZOOM_LEVELS.findIndex(
          zoomLevel => zoomLevel === scene.game.defaultZoom
        ) + 1
      ];
    const x = cam.scrollX + cam.centerX;
    const y = cam.scrollY + cam.centerY;
    const partyPortraits = joinsPartyPortraits[command.character];

    const destinations = {
      outerStripe: { x: x, y: y + 54 * scale },
      innerStripe: { x: x, y: y + 45 * scale },
      text: { x: x + 75 * scale, y: y + 72 * scale },
      portrait: { x: x + 174 * scale, y: y + 83 * scale },
      name: { x: x + 48 * scale, y: y + 42 * scale }
    };

    menuScene.joinsPartyOuterStripe = menuScene.add
      .image(
        destinations.outerStripe.x - cam.worldView.width * 2,
        destinations.outerStripe.y,
        'ui_joins_party_outer_stripe'
      )
      .setScale(scale)
      .setDepth(24);
    // addDebugDragging(menuScene, menuScene.joinsPartyOuterStripe);
    menuScene.joinsPartyInnerStripe = menuScene.add
      .image(
        destinations.innerStripe.x - cam.worldView.width * 2,
        destinations.innerStripe.y,
        'ui_joins_party_inner_stripe'
      )
      .setScale(scale)
      .setDepth(24);
    // addDebugDragging(menuScene, menuScene.joinsPartyInnerStripe);
    menuScene.joinsPartyText = menuScene.add
      .image(
        destinations.text.x - cam.worldView.width * 2,
        destinations.text.y,
        'ui_joins_party_joins_party'
      )
      .setScale(scale)
      .setDepth(25);
    // addDebugDragging(menuScene, menuScene.joinsPartyText);
    menuScene.joinsPartyPortrait = menuScene.add
      .image(
        destinations.portrait.x,
        destinations.portrait.y,
        partyPortraits.portrait
      )
      .setScale(scale, 0)
      .setOrigin(0.5, 1)
      .setDepth(25);
    // addDebugDragging(menuScene, menuScene.joinsPartyPortrait);
    menuScene.joinsPartyName = menuScene.add
      .image(
        destinations.name.x - cam.worldView.width * 2,
        destinations.name.y,
        partyPortraits.name
      )
      .setScale(scale)
      .setDepth(25);
    // addDebugDragging(menuScene, menuScene.joinsPartyName);
    //start animation?
    menuScene.tweens.add({
      targets: [menuScene.joinsPartyOuterStripe],
      x: destinations.outerStripe.x,
      duration: 350,
      ease: 'Expo.easeIn',
      onComplete: () => {}
    });
    menuScene.tweens.add({
      targets: [menuScene.joinsPartyInnerStripe],
      x: destinations.innerStripe.x,
      duration: 350,
      ease: 'Expo.easeIn',
      delay: 250,
      onComplete: () => {}
    });
    menuScene.tweens.add({
      targets: [menuScene.joinsPartyPortrait],
      scaleY: scale,
      duration: 150,
      ease: 'Bounce.easeOut',
      delay: 450,
      onComplete: () => {}
    });
    menuScene.tweens.add({
      targets: [menuScene.joinsPartyText],
      x: destinations.text.x,
      duration: 350,
      ease: 'Expo.easeIn',
      delay: 650,
      onComplete: () => {}
    });
    menuScene.tweens.add({
      targets: [menuScene.joinsPartyName],
      x: destinations.name.x,
      duration: 350,
      ease: 'Expo.easeIn',
      delay: 950,
      onComplete: () => {
        // wait a short time, then animate out
        setTimeout(() => {
          menuScene.joinsPartyOuterStripe.destroy();
          menuScene.joinsPartyInnerStripe.destroy();
          menuScene.joinsPartyPortrait.destroy();
        }, 2900);
        menuScene.tweens.add({
          targets: [menuScene.joinsPartyText],
          x: destinations.text.x + cam.worldView.width * 2,
          duration: 350,
          ease: 'Expo.easeIn',
          delay: 2650,
          onComplete: () => {}
        });
        menuScene.tweens.add({
          targets: [menuScene.joinsPartyName],
          x: destinations.name.x + cam.worldView.width * 2,
          duration: 350,
          ease: 'Expo.easeIn',
          delay: 2650,
          onComplete: () => {
            // apply clickable overlay to aniamte out, and move on
            menuScene.joinsPartyName.destroy();
            menuScene.joinsPartyText.destroy();
            // move on in command chain
            next();
          }
        });
      }
    });
  });
}

export function addDebugDragging(scene, sprite) {
  scene.input.setDraggable(sprite);

  scene.input.on('dragstart', (pointer, gameObject) => {
    gameObject.setTint(0xff0000);
  });

  scene.input.on('drag', (pointer, gameObject, dragX, dragY) => {
    gameObject.x = dragX;
    gameObject.y = dragY;
    console.log(
      'Sprite/Position:',
      gameObject.texture.key,
      gameObject.x,
      gameObject.y
    );
  });

  scene.input.on('dragend', (pointer, gameObject) => {
    gameObject.clearTint();
  });
}
